// render_expenses.js
import { formatDate, formatCurrency } from './utils.mjs';
import { renderModal } from './ui.mjs';
import router from './router.mjs';

/**
 * Curated purple/teal palette — same as renderHome.mjs / Flutter app.
 */
const FLUTTER_AVATAR_COLORS = [
    '#20B2AA', '#48D1CC', '#DDA0DD', '#DA70D6',
    '#BA55D3', '#9370DB', '#5F9EA0', '#9B87F5',
    '#00CED1', '#40E0D0', '#AB82C5', '#9F7AEA',
];

function getAvatarColor(str) {
    if (!str) return FLUTTER_AVATAR_COLORS[0];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return FLUTTER_AVATAR_COLORS[Math.abs(hash) % FLUTTER_AVATAR_COLORS.length];
}

/**
 *
 * @param {HTMLElement} parentEl
 * @param {Group} group
 */
// Module-level page index — survives re-renders so closing a modal doesn't
// reset the user back to page 1. Guarded below against out-of-bounds.
let _savedPage = 0;

export function renderExpenseCards(parentEl, group) {

    let expenses = group.expenses;
    let pageSize = 4;

    const totalPages = Math.ceil(expenses.length / pageSize);

    // Restore the last-known page; clamp if the data set shrank since last render.
    let currentPage = (_savedPage < totalPages) ? _savedPage : 0;

    function renderCardsAndControls() {
        parentEl.innerHTML = ''; // Clear the parent element

        // Create container for expense cards
        const expensesContainer = document.createElement('div');
        expensesContainer.id = 'expenses-container';

        const start = currentPage * pageSize;
        const end = start + pageSize;
        const expensesToShow = expenses.slice(start, end);

        expensesToShow.forEach(expense => {
            const cardEl = document.createElement('div');
            cardEl.className = 'list-item';
            cardEl.dataset.expenseId = expense.id;

            // Resolve payer name & build monogram avatar
            const payerIndex = (expense.payers && expense.payers.length > 0) ? expense.payers[0] : null;
            const hasPayer = payerIndex !== null && group.participants && group.participants[payerIndex];
            const payerName = hasPayer
                ? group.participants[payerIndex]
                : (expense.description || '?');
            const avatarColor = getAvatarColor(payerName);
            const avatarLetter = hasPayer ? payerName.charAt(0).toUpperCase() : '?';

            const avatarHtml = `<div class="tab-avatar" style="background-color:${avatarColor}; flex-shrink:0;">${avatarLetter}</div>`;

            // Sort participants so payers appear first
            const payerIndices = new Set(expense.payers || []);
            const sortedParticipants = [...expense.participants].sort((a, b) => {
                const aIsPayer = payerIndices.has(a);
                const bIsPayer = payerIndices.has(b);
                if (aIsPayer && !bIsPayer) return -1;
                if (!aIsPayer && bIsPayer) return 1;
                return 0;
            });

            const participantNames = sortedParticipants.map(pIndex => group.participants[pIndex]).join(', ');

            // Mirror Flutter's getMonetaryValue(amount, currency, rate, baseCurrency):
            // If the expense has a conversion rate and its currency differs from the group's
            // base currency, show "¥42,570 (£200.00)" — converted + original in parens.
            const baseCurrency = group.currency;
            const hasConversion = expense.rate && expense.rate !== 1
                && expense.currency && baseCurrency
                && expense.currency !== baseCurrency;
            const formattedAmount = hasConversion
                ? `<span class="expense-amount-base">${formatCurrency(expense.amount / expense.rate, baseCurrency)}</span><span class="expense-original-currency">${formatCurrency(expense.amount, expense.currency)}</span>`
                : formatCurrency(expense.amount, expense.currency);

            // Receipt icon badge for itemized expenses (small, inline with title)
            const itemizedBadge = expense.expenseType === 'ITEMIZED'
                ? `<svg style="width:14px;height:14px;fill:#aaa;flex-shrink:0;margin-left:4px;" viewBox="0 0 24 24"><path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 6H7V7h10v2zm-4 4H7v-2h6v2zm4 4H7v-2h10v2z"/></svg>`
                : '';

            cardEl.innerHTML = `
                <div class="list-item-leading">${avatarHtml}</div>
                <div class="list-item-content">
                    <div class="list-item-title" style="display:flex;align-items:center;gap:4px;">${expense.description}${itemizedBadge}</div>
                    <div class="list-item-subtitle">${participantNames}</div>
                    <div class="list-item-date">${formatDate(expense.date)}${!hasPayer ? ' <span class="unassigned-label">(who paid?)</span>' : ''}</div>
                </div>
                <div class="list-item-trailing">
                    <div class="list-item-amount">${formattedAmount}</div>
                </div>
            `;
            cardEl.onclick = () => {
                if (expense.expenseType === 'ITEMIZED') {
                    router.navigate(`/home/${group.id}/receipt/${expense.id}`);
                } else {
                    // Navigate to simple expense URL
                    router.navigate(`/home/${group.id}/simple/${expense.id}`);
                }
            };
            expensesContainer.appendChild(cardEl);
        });

        parentEl.appendChild(expensesContainer);

        // Create pagination controls
        if (totalPages > 1) {
            const paginationEl = document.createElement('div');
            paginationEl.id = 'pagination-controls';

            const prevButton = document.createElement('button');
            prevButton.className = 'pagination-arrow';
            prevButton.innerHTML = `<svg viewBox="0 0 24 24"><path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z"/></svg>`;
            prevButton.disabled = currentPage === 0;
            prevButton.setAttribute('aria-label', 'Previous page');
            prevButton.onclick = () => { currentPage--; _savedPage = currentPage; renderCardsAndControls(); };

            const pageLabel = document.createElement('span');
            pageLabel.className = 'pagination-label';
            pageLabel.textContent = `${currentPage + 1} of ${totalPages}`;

            const nextButton = document.createElement('button');
            nextButton.className = 'pagination-arrow';
            nextButton.innerHTML = `<svg viewBox="0 0 24 24"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/></svg>`;
            nextButton.disabled = currentPage === totalPages - 1;
            nextButton.setAttribute('aria-label', 'Next page');
            nextButton.onclick = () => { currentPage++; _savedPage = currentPage; renderCardsAndControls(); };

            paginationEl.appendChild(prevButton);
            paginationEl.appendChild(pageLabel);
            paginationEl.appendChild(nextButton);

            parentEl.appendChild(paginationEl);
        }
    }

    // Initial render
    renderCardsAndControls();
}